// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function VoiceChangerProTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="Voice Changer Pro"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of “Voice
        Changer Pro” (the “App”). By downloading, installing, accessing, or
        using the App, you agree to be bound by these Terms. If you do not
        agree, you must uninstall the App and discontinue all use. Where
        required by platform rules or local law, additional terms may apply; in
        case of conflict with App Store or Google Play terms, the platform terms
        may prevail to the extent required.
      </p>

      <div className={s.title}>2) Description of the App</div>
      <p>
        The App allows you to record voice, import audio from your device, apply
        various sound effects (such as pitch shifting, robot, chipmunk, echo,
        reverb, and other presets), and save or share the processed result. Free
        use may be limited to a small number of effects and saved files, while
        paid subscriptions may unlock additional effects, higher limits, and
        export quality options. The App is intended for entertainment and
        creative purposes only and does not provide professional audio
        engineering, legal, or any other professional services.
      </p>

      <div className={s.title}>3) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction. Where parental
        consent is required by law, a parent or guardian must approve use. By
        using the App, you confirm that you meet applicable age and eligibility
        requirements or have obtained the necessary consent.
      </p>

      <div className={s.title}>4) Accounts, Registration, and Security</div>
      <p>
        The App may require or offer registration using Firebase Authentication
        or similar services (for example, sign-in with email, Google, or Apple
        ID). You are responsible for maintaining the confidentiality of your
        account credentials and for all activity that occurs under your account.
        You must promptly notify us of any suspected unauthorized access or
        security issues. We may suspend or restrict access if we reasonably
        believe your account has been compromised or used in violation of these
        Terms.
      </p>

      <div className={s.title}>5) Microphone, Audio Files, and Permissions</div>
      <p>
        To record voice and process audio stored on your device, the App may
        request permissions to access the microphone, local storage, and media
        libraries. You can choose whether to grant or revoke these permissions
        in your device settings. If you deny permissions, some or all features
        may be unavailable.
      </p>
      <p>
        When you record or import audio, the App processes this content locally
        or via integrated libraries to apply effects and create output files.
        Recordings and processed files are typically stored in local app storage
        on your device, and may be listed in the App’s “history” or similar
        screens. Deleting the App or clearing its data may permanently remove
        stored audio files and history.
      </p>

      <div className={s.title}>6) User Content and Responsibility</div>
      <p>
        The audio recordings, imported files, and processed outputs you create
        (“User Content”) remain yours or your licensors’. You are solely
        responsible for ensuring that you have all necessary rights and
        permissions to record, modify, store, and share User Content, including
        permissions from people whose voices are recorded and any owners of
        third-party audio or music.
      </p>
      <p>
        You must not use the App to create or distribute audio that is illegal,
        hateful, infringing, or intended to harass, deceive, or harm others. You
        must not use processed voice to impersonate other people in ways that
        could cause fraud, confusion, or reputational damage.
      </p>

      <div className={s.title}>7) Free Use, Limits, and Storage</div>
      <p>
        The App may offer a free tier with limited functionality—for example, a
        restricted number of available effects, a cap on how many processed
        files you can store in history, or limits on export quality. When these
        limits are reached, you may need to delete existing items, wait for
        reset (if applicable), or upgrade to a subscription to continue using
        certain features.
      </p>
      <p>
        Limits and counters may be stored locally on your device and/or
        associated with your account. Deleting or reinstalling the App does not
        guarantee that limits will reset, as some limits may be accounted for at
        the server or subscription level.
      </p>

      <div className={s.title}>8) Subscriptions, Trials, and Billing</div>
      <p>
        Premium features—such as access to all voice effects, higher export
        quality, and increased or unlimited storage of processed files—may be
        offered through auto-renewable subscriptions or other in-app purchases
        (“Subscriptions”), such as weekly or yearly plans. Exact pricing,
        durations, and offers (including any free trial) may vary by region and
        may change over time as displayed in the App and on the paywall screen.
      </p>
      <p>
        Purchases are processed by Apple or Google via your store account; we do
        not receive your payment card numbers. If a free trial is offered, it
        will convert into a paid Subscription unless cancelled at least 24 hours
        before the end of the trial period in your account settings. You can
        manage or cancel Subscriptions directly in platform subscription
        settings. Deleting the App does not automatically cancel a Subscription.
        Refunds, grace periods, and other billing matters are determined by the
        policies of the relevant app store.
      </p>

      <div className={s.title}>9) Entitlements and Access Control</div>
      <p>
        Subscription status and eligibility may be managed using services such
        as RevenueCat and platform billing APIs. We rely on entitlement data
        (for example, product identifiers, trial eligibility, renewal and
        expiration status) to determine whether you have access to premium
        features. An internet connection may be required to validate or refresh
        entitlements after long offline periods. If platform billing reverses,
        refunds, or flags a transaction as fraudulent, related entitlements may
        be limited, suspended, or revoked.
      </p>

      <div className={s.title}>10) Exports, Sharing, and Third-Party Apps</div>
      <p>
        The App may allow you to export processed audio files (for example, MP3
        or WAV) and share them via other apps or services (such as messaging
        apps, social networks, or cloud storage). Once you export or share
        content outside the App, that content is governed by the terms and
        privacy policies of the receiving platforms, which we do not control.
      </p>
      <p>
        You are responsible for how and where you share processed audio and for
        ensuring that your sharing complies with applicable laws and third-party
        terms. We are not responsible for actions taken by other users or
        platforms with respect to your shared content.
      </p>

      <div className={s.title}>11) Acceptable Use and Prohibited Conduct</div>
      <p>
        You agree to use the App only for lawful, respectful, and
        non-harmful purposes and not to:
      </p>
      <ul>
        <li>
          record or upload audio that is illegal, exploitative, hateful,
          harassing, or otherwise violates applicable law or platform rules;
        </li>
        <li>
          create or share processed audio intended to impersonate others in
          contexts that could cause fraud, defamation, or serious misleading;
        </li>
        <li>
          infringe intellectual property rights, privacy rights, or other rights
          of any person;
        </li>
        <li>
          attempt to reverse engineer, decompile, or extract the App&apos;s
          source code except where permitted by law;
        </li>
        <li>
          circumvent technical protections, feature limits, or paywalls
          separating free and premium features;
        </li>
        <li>
          use automated tools or scripts to overload or misuse the App or its
          backend services.
        </li>
      </ul>
      <p>
        If we reasonably believe your use violates these rules or applicable
        law, we may restrict, suspend, or terminate your access and, where
        required, cooperate with platform operators or authorities.
      </p>

      <div className={s.title}>12) Intellectual Property</div>
      <p>
        You retain ownership of your original audio recordings, imported files,
        and processed outputs, subject to rights of third parties. By using the
        App, you grant us and our service providers a limited, non-exclusive,
        worldwide, royalty-free license to process and store User Content as
        needed to operate, maintain, and improve the App (for example, to apply
        effects, store history, and show recent files).
      </p>
      <p>
        All software, user interface designs, effect presets, names, logos, and
        other materials provided with the App (excluding User Content and
        third-party libraries) are owned by the publisher or its licensors and
        are protected by intellectual property laws. Subject to these Terms and
        any Subscription limitations, you are granted a limited, non-exclusive,
        non-transferable, revocable license to use the App and its features for
        personal and permitted creative use.
      </p>

      <div className={s.title}>13) Third-Party Services and Libraries</div>
      <p>
        The App may use third-party libraries and services for audio recording,
        playback, signal processing, analytics, crash reporting, and
        subscription management. Examples can include audio SDKs, FFmpeg-based
        components, Firebase Analytics, Crashlytics, and RevenueCat. Technical
        data and usage information may be shared with these providers to
        operate, secure, and improve the App. These providers act under their
        own terms and privacy policies, and we require them by contract to
        handle data appropriately and only for permitted purposes.
      </p>

      <div className={s.title}>14) Analytics and Performance Monitoring</div>
      <p>
        We may use analytics tools (for example, Firebase Analytics) to
        understand how the App is used, measure feature adoption, and improve
        reliability and user experience. Typical events may include app
        launches, recording starts and stops, effect selections, exports,
        paywall views, and purchase outcomes. Aggregated or de-identified data
        may be used for product decisions. Where required by law, we will obtain
        necessary permissions or provide options to manage analytics
        preferences.
      </p>

      <div className={s.title}>15) No Warranties</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and non-infringement.
        We do not warrant that:
      </p>
      <ul>
        <li>
          all audio will record, process, or export correctly on every device;
        </li>
        <li>
          all effects will work without artifacts, noise, or distortion in every
          scenario;
        </li>
        <li>
          the App will be available without interruption, errors, or security
          issues.
        </li>
      </ul>

      <div className={s.title}>16) Limitation of Liability</div>
      <p>
        To the extent allowed by law, we are not liable for any indirect,
        incidental, consequential, or punitive damages, loss of profits, loss of
        data (including loss of recordings, processed files, or history), or
        business interruption arising from or related to your use of or
        inability to use the App. Our total aggregate liability to you for
        direct damages arising out of or relating to the App will be limited to
        the amount you paid for Subscriptions during the twelve (12) months
        prior to the claim, or the minimum amount permitted by applicable law if
        greater. Some jurisdictions do not allow certain limitations or
        exclusions, so parts of this section may not apply to you.
      </p>

      <div className={s.title}>17) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your User Content, your sharing of
        processed audio, your breach of these Terms, or your violation of any
        rights of a third party.
      </p>

      <div className={s.title}>18) Termination</div>
      <p>
        We may suspend or terminate your access to the App or to specific
        features at any time if we reasonably believe you have violated these
        Terms, misused the App, or where required by law, platform rules, or
        security concerns. You may stop using the App at any time by
        uninstalling it and, where applicable, cancelling your Subscription via
        platform settings. Sections that by their nature should survive
        termination (for example, intellectual property, acceptable use,
        limitation of liability, and indemnification) will continue to apply
        after your access ends.
      </p>

      <div className={s.title}>19) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        Voice Changer Pro, which explains how information is collected, used,
        stored, and shared, including technical and analytics data. In case of
        conflict between these Terms and the Privacy Policy regarding data
        processing, the Privacy Policy will guide how we handle personal data,
        while these Terms govern your use of the App and its features. You
        should review the Privacy Policy regularly to understand current
        practices.
      </p>

      <div className={s.title}>20) Changes to These Terms and Contact</div>
      <p>
        We may update the App, add or remove features (including changes to free
        and premium offerings), or modify these Terms from time to time. The
        “Last updated” date at the top of this document indicates when these
        Terms were last revised. Material changes may be highlighted in the App
        or via other reasonable means. By continuing to use the App after
        changes take effect, you accept the updated Terms. If you do not agree
        to the revised Terms, you must stop using the App and uninstall it.
      </p>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing account-specific
        or purchase-related matters.
      </p>
    </TermOfUseLayout>
  );
}
